import type {
  AddProponentDataRequest,
  BatchProponentItem,
  BeneficiaryInclusionItem,
  BeneficiaryUpdateItem,
  CalculateProponentPremiumRequest,
  CalculateProponentPremiumResponse,
  GetProponentListParams,
  ImportProponentBatchRequest,
  ProponentDataReturn,
  ProponentDetail,
  ProponentInclusionItem,
  ProponentListItem,
  UpdateProponentDataRequest,
} from '../types/vida/inclusionProponent/inclusionProponentType';
import type { ListaSubestipulanteItem } from '../types/vida/subEstipulante/subEstipulanteType';
import type { ProponentFormValues } from '../types/vida/cadastralMaintenance/proponentFormTypes';
import type { InclusionProponenteTableItem } from '../types/vida/table/inclusionProponentesTableType';
import { formatCepForInput, parseCepParts } from './cepFieldUtils';
import {
  formatMoneyDecimalForApi,
  formatMoneyForFormDisplay,
  formatSalaryDecimalForApi,
  formatSalaryForFormInitial,
} from './insuredFormUtils';
import { mapProponentStatus } from './proponentStatusUtils';

const SEX_LABELS: Record<string, string> = {
  '1': 'Masculino',
  '2': 'Feminino',
};

export const mapProponentSex = (sexo: string): string => SEX_LABELS[sexo] ?? sexo;

export const formatProponentBirthDate = (dtNascimento: string): string => {
  const trimmed = dtNascimento.trim();
  if (!trimmed) {
    return '';
  }

  const apiMatch = /^(\d{4})\/(\d{2})\/(\d{2})$/.exec(trimmed);
  if (apiMatch) {
    return `${apiMatch[3]}/${apiMatch[2]}/${apiMatch[1]}`;
  }

  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (isoMatch) {
    return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
  }

  const brMatch = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(trimmed);
  if (brMatch) {
    return trimmed;
  }

  const compactMatch = /^(\d{4})(\d{2})(\d{2})$/.exec(trimmed);
  if (compactMatch) {
    return `${compactMatch[3]}/${compactMatch[2]}/${compactMatch[1]}`;
  }

  return dtNascimento;
};

export const formatProponentBirthDateInputMask = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) {
    return digits;
  }
  if (digits.length <= 4) {
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  }
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

export const formatDateForDateInput = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  const slashApiMatch = /^(\d{4})\/(\d{2})\/(\d{2})$/.exec(trimmed);
  if (slashApiMatch) {
    return `${slashApiMatch[1]}-${slashApiMatch[2]}-${slashApiMatch[3]}`;
  }

  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (isoMatch) {
    return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  }

  const brMatch = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(trimmed);
  if (brMatch) {
    return `${brMatch[3]}-${brMatch[2]}-${brMatch[1]}`;
  }

  const compactMatch = /^(\d{4})(\d{2})(\d{2})$/.exec(trimmed);
  if (compactMatch) {
    return `${compactMatch[1]}-${compactMatch[2]}-${compactMatch[3]}`;
  }

  const timestampMatch = /^(\d{4}-\d{2}-\d{2})T/.exec(trimmed);
  if (timestampMatch) {
    return timestampMatch[1];
  }

  return '';
};

export const formatCpfFromParts = (cpf: number, cpfControle: number): string => {
  const digits = `${String(cpf).padStart(9, '0')}${String(cpfControle).padStart(2, '0')}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

export const formatProponentDateForInput = (dataNascimento: string): string =>
  formatProponentBirthDate(dataNascimento);

export const formatDateForFormDisplay = formatProponentBirthDate;

export { parseCepParts } from './cepFieldUtils';

const SEX_CODE_BY_LABEL: Record<string, string> = {
  Masculino: '1',
  Feminino: '2',
};

export const DEFAULT_PROPONENT_ORIGEM_INFORMACAO = 1;
export const DEFAULT_PROPONENT_CD_PERFIL_ACESSO = 1;
export const LAYOUT_IMPORT_COMPLETO_CODE = 4;
export const LAYOUT_IMPORT_REDUZIDO_CODE = 2;
export const PROPONENT_BATCH_SISTEMA_ORIGEM = 'BVPVIDA';

export type LayoutFieldDefinition = {
  key: string;
  label: string;
};

export const FALLBACK_LAYOUT_FIELDS: LayoutFieldDefinition[] = [
  { key: 'matricula', label: 'Matrícula' },
  { key: 'nome', label: 'Nome' },
  { key: 'dataNascimento', label: 'Data de Nascimento' },
  { key: 'sexo', label: 'Sexo' },
  { key: 'cpf', label: 'Cpf' },
  { key: 'endereco', label: 'Endereço' },
  { key: 'numero', label: 'Número' },
  { key: 'bairro', label: 'Bairro' },
  { key: 'cidade', label: 'Cidade' },
  { key: 'cep', label: 'CEP' },
  { key: 'uf', label: 'UF' },
  { key: 'salario', label: 'Salário' },
];

export const REDUZIDO_LAYOUT_FIELD_KEYS = [
  'matricula',
  'nome',
  'cpf',
  'dataNascimento',
  'sexo',
  'salario',
];

export const mapUiLayoutTypeToImportCode = (layoutType: 'completo' | 'reduzido'): number =>
  layoutType === 'completo' ? LAYOUT_IMPORT_COMPLETO_CODE : LAYOUT_IMPORT_REDUZIDO_CODE;

export const mapEmissionTypeToMovtoManut = (emissionType: 'normal' | 'complementar'): string =>
  emissionType === 'complementar' ? 'C' : 'E';

export const resolveLayoutImportParams = (
  subestipulante: ListaSubestipulanteItem,
): { pcContrApolc: number; pcSubEstteApolc: number } => ({
  pcContrApolc: subestipulante.identContratoOut,
  pcSubEstteApolc: subestipulante.codSubestipulanteOut,
});

export const getActiveLayoutFields = (
  layoutType: 'completo' | 'reduzido',
  fields: LayoutFieldDefinition[] = FALLBACK_LAYOUT_FIELDS,
): LayoutFieldDefinition[] =>
  layoutType === 'completo'
    ? fields
    : fields.filter(({ key }) => REDUZIDO_LAYOUT_FIELD_KEYS.includes(key));

export const parseLayoutImportFields = (
  layouts: string | null | undefined,
): LayoutFieldDefinition[] | null => {
  if (!layouts?.trim()) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(layouts);
    if (!Array.isArray(parsed)) {
      return null;
    }

    return parsed.map((item, index) => {
      if (typeof item === 'string') {
        return { key: `col_${index}`, label: item };
      }

      if (typeof item !== 'object' || item == null) {
        return { key: `col_${index}`, label: `Coluna ${index + 1}` };
      }

      const record = item as Record<string, unknown>;
      const key = String(record.campo ?? record.codigo ?? record.key ?? record.nome ?? `col_${index}`);
      const label = String(record.label ?? record.descricao ?? record.nome ?? record.campo ?? key);

      return { key, label };
    });
  } catch {
    return null;
  }
};

const normalizeFieldLabel = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/\s+/g, '');

export const buildAutoFieldMapping = (
  layoutFields: LayoutFieldDefinition[],
  fileColumns: string[],
): Record<string, string> => {
  const mapping: Record<string, string> = {};

  layoutFields.forEach(({ key, label }) => {
    const target = normalizeFieldLabel(label);
    const match = fileColumns.find((col) => {
      const normalizedCol = normalizeFieldLabel(col);
      return normalizedCol === target
        || normalizedCol.includes(target)
        || target.includes(normalizedCol);
    });
    mapping[key] = match ?? '';
  });

  return mapping;
};

export const isCsvFile = (file: File): boolean => {
  const lowerName = file.name.toLowerCase();
  return lowerName.endsWith('.csv') || file.type === 'text/csv';
};

export const parseSpreadsheetRows = (
  content: string,
  delimiter: string,
  headerLineCount: number,
  footerLineCount: number,
): string[][] => {
  const lines = content
    .split(/\r?\n/)
    .filter((line) => line.trim() !== '');

  const footerCount = Math.max(0, footerLineCount);
  const endIndex = footerCount > 0 ? lines.length - footerCount : lines.length;
  const dataLines = lines.slice(Math.max(0, headerLineCount), endIndex);
  const delim = delimiter === '\\t' ? '\t' : delimiter;

  return dataLines.map((line) =>
    line.split(delim).map((cell) => cell.trim().replace(/(^")|("$)/g, '')),
  );
};

export const extractFileColumnOptions = (
  rows: string[][],
  hasHeader: boolean,
): { columns: string[]; dataRows: string[][]; previewRow: Record<string, string> } => {
  if (rows.length === 0) {
    return { columns: [], dataRows: [], previewRow: {} };
  }

  let columns: string[];
  let dataRows: string[][];

  if (hasHeader) {
    columns = rows[0].map((col, index) => col.trim() || `Coluna ${index + 1}`);
    dataRows = rows.slice(1);
  } else {
    const colCount = rows[0]?.length ?? 0;
    columns = Array.from({ length: colCount }, (_, index) => `Coluna ${index + 1}`);
    dataRows = rows;
  }

  const previewRow: Record<string, string> = {};
  if (dataRows[0]) {
    columns.forEach((col, index) => {
      previewRow[col] = dataRows[0][index] ?? '';
    });
  }

  return { columns, dataRows, previewRow };
};

const parseSpreadsheetDateToApi = (dateStr: string): string => {
  const trimmed = dateStr.trim();
  const slashBr = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(trimmed);
  if (slashBr) {
    return `${slashBr[3]}${slashBr[2]}${slashBr[1]}`;
  }

  const dashIso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (dashIso) {
    return `${dashIso[1]}${dashIso[2]}${dashIso[3]}`;
  }

  const digits = trimmed.replace(/\D/g, '');
  if (digits.length === 8) {
    return digits;
  }

  return digits;
};

const parseSpreadsheetCpfParts = (cpfStr: string): { cdCpf: number; cdCtrlCpf: number } => {
  const digits = cpfStr.replace(/\D/g, '');
  return {
    cdCpf: Number(digits.slice(0, 9)) || 0,
    cdCtrlCpf: Number(digits.slice(9, 11)) || 0,
  };
};

const parseSpreadsheetCepParts = (cepStr: string): { cepPrefixo: number; cepSuxifo: number } => {
  const digits = cepStr.replace(/\D/g, '');
  return {
    cepPrefixo: Number(digits.slice(0, 5)) || 0,
    cepSuxifo: Number(digits.slice(5, 8)) || 0,
  };
};

const parseSpreadsheetNumber = (value: string): number => {
  const normalized = value.replace(/\./g, '').replace(',', '.').trim();
  const num = Number(normalized);
  return Number.isFinite(num) ? num : 0;
};

export const buildBatchProponentItemsFromSpreadsheet = (params: {
  dataRows: string[][];
  columns: string[];
  fieldMapping: Record<string, string>;
  headerLineCount: number;
}): BatchProponentItem[] => {
  const { dataRows, columns, fieldMapping, headerLineCount } = params;

  const getCell = (row: string[], layoutKey: string): string => {
    const colName = fieldMapping[layoutKey];
    if (!colName) {
      return '';
    }
    const idx = columns.indexOf(colName);
    return idx >= 0 ? (row[idx] ?? '').trim() : '';
  };

  return dataRows
    .filter((row) => row.some((cell) => cell.trim() !== ''))
    .map((row, index) => {
      const cpf = parseSpreadsheetCpfParts(getCell(row, 'cpf'));
      const cep = parseSpreadsheetCepParts(getCell(row, 'cep'));

      return {
        nuLinhaRegistro: headerLineCount + index + 1,
        nmProponente: getCell(row, 'nome'),
        cdCpf: cpf.cdCpf,
        cdCtrlCpf: cpf.cdCtrlCpf,
        nuDocto: '',
        cdSexo: mapSexLabelToCode(getCell(row, 'sexo'), '1'),
        cdEstCivil: 0,
        dtNascto: parseSpreadsheetDateToApi(getCell(row, 'dataNascimento')),
        cepPrefixo: cep.cepPrefixo,
        cepSuxifo: cep.cepSuxifo,
        dsLogradouro: getCell(row, 'endereco'),
        nuLogradouro: getCell(row, 'numero') || '0',
        dsComplemento: '',
        dsBairro: getCell(row, 'bairro'),
        dsCidade: getCell(row, 'cidade'),
        dsUF: getCell(row, 'uf'),
        cdMatricula: getCell(row, 'matricula'),
        dsCargo: '',
        vrSalario: parseSpreadsheetNumber(getCell(row, 'salario')),
        dtAdmissao: '',
        vrCapitalSeg: 0,
        vrPremioSeg: 0,
      };
    });
};

export const buildImportProponentBatchRequest = (params: {
  identificadorSubestipulante: number;
  codigoUsuarioInclusao: string;
  layoutImportCode: number;
  cTpoMovtoManut: string;
  protocoloId: number;
  jsonProponente: BatchProponentItem[];
  origemInformacao?: number;
  usuarioLogado?: string;
  idDaSolicitacao?: string;
}): ImportProponentBatchRequest => {
  const idDaSolicitacao = params.idDaSolicitacao
    ?? (typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `import-${Date.now()}`);

  return {
    informacoesDeControle: {
      sistemaDeOrigem: PROPONENT_BATCH_SISTEMA_ORIGEM,
      usuarioLogado: params.usuarioLogado ?? params.codigoUsuarioInclusao,
      idDaSolicitacao,
    },
    enviarPlanilhaDeProponente: {
      identificadorSubestipulante: params.identificadorSubestipulante,
      codigoUsuarioInclusao: params.codigoUsuarioInclusao,
      origemInformacao: params.origemInformacao ?? DEFAULT_PROPONENT_ORIGEM_INFORMACAO,
      pLyoutImport: params.layoutImportCode,
      cTpoMovtoManut: params.cTpoMovtoManut,
      protocoloId: params.protocoloId,
      jsonProponente: params.jsonProponente,
    },
  };
};

export const resolveCodigoUsuarioInclusao = (idPessoa?: number): string => {
  if (idPessoa != null && Number.isFinite(idPessoa)) {
    return String(idPessoa);
  }
  return 'ADMIN';
};

export const formatFormDateToApi = (dataNascimento: string): string => {
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dataNascimento.trim());
  if (isoMatch) {
    return `${isoMatch[1]}${isoMatch[2]}${isoMatch[3]}`;
  }

  const brMatch = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(dataNascimento.trim());
  if (brMatch) {
    return `${brMatch[3]}${brMatch[2]}${brMatch[1]}`;
  }

  return dataNascimento.replace(/\D/g, '');
};

export const mapSexLabelToCode = (sexoBiologico: string, fallback = '1'): string => {
  const normalized = sexoBiologico.trim();
  if (SEX_CODE_BY_LABEL[normalized]) {
    return SEX_CODE_BY_LABEL[normalized];
  }
  if (/^\d+$/.test(normalized)) {
    return normalized;
  }
  return fallback;
};

export const mapFormValuesToProponentInclusionItem = (
  values: ProponentFormValues,
  detail: ProponentDetail,
): ProponentInclusionItem => {
  const cpfDigits = values.cpf.replace(/\D/g, '');

  return {
    nmProponente: values.nome.trim(),
    cdCpf: cpfDigits.slice(0, 9),
    cdCtrlCpf: cpfDigits.slice(9, 11),
    nuDocto: values.documento.trim(),
    cdSexo: mapSexLabelToCode(values.sexo, detail.sexo),
    cdEstCivil: Number(values.estadoCivil) || detail.estadoCivil || 0,
    dtNascto: formatFormDateToApi(values.dataNascimento),
    cepPrefixo: parseCepParts(values.cep).cepPrefixo || '0',
    cepSuxifo: parseCepParts(values.cep).cepSufixo || '0',
    dsLogradouro: values.logradouro.trim(),
    nuLogradouro: values.numeroLogradouro.trim() || '0',
    dsComplemento: values.complemento.trim(),
    dsBairro: values.bairro.trim(),
    dsCidade: values.cidade.trim(),
    dsUF: values.uf.trim(),
    cdMatricula: values.matricula.trim(),
    dsCargo: values.cargo.trim(),
    vrCapitalSeg: formatSalaryDecimalForApi(values.capital),
    vrPremioSeg: formatMoneyDecimalForApi(values.premio),
    vrSalario: formatSalaryDecimalForApi(values.salario),
  };
};

export const buildUpdateProponentDataRequest = (params: {
  identificadorSubestipulante: number;
  numeroProponente: number;
  codigoUsuarioInclusao: string;
  formValues: ProponentFormValues;
  detail: ProponentDetail;
  origemInformacao?: number;
  cdPerfilAcesso?: number;
  beneficiarios?: BeneficiaryUpdateItem[];
}): UpdateProponentDataRequest => ({
  identificadorSubestipulante: params.identificadorSubestipulante,
  codigoUsuarioInclusao: params.codigoUsuarioInclusao,
  origemInformacao: params.origemInformacao ?? DEFAULT_PROPONENT_ORIGEM_INFORMACAO,
  numeroProponente: params.numeroProponente,
  cdPerfilAcesso: params.cdPerfilAcesso ?? DEFAULT_PROPONENT_CD_PERFIL_ACESSO,
  jsonProponente: JSON.stringify([
    mapFormValuesToProponentInclusionItem(params.formValues, params.detail),
  ]),
  jsonBeneficiario: JSON.stringify(params.beneficiarios ?? []),
});

export const mapSexToPremiumCode = (sexo: string): string => {
  const code = mapSexLabelToCode(sexo, '1');
  return code === '2' ? 'F' : 'M';
};

export const getTodayCompactDate = (): string => {
  const now = new Date();
  const year = String(now.getFullYear());
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
};

export const buildCalculateProponentPremiumRequest = (params: {
  idContrato: number;
  idSubestipulante: string;
  idProtocolo: number;
  formValues: ProponentFormValues;
}): CalculateProponentPremiumRequest => {
  const dataInclusao = getTodayCompactDate();

  return {
    idContrato: params.idContrato,
    idSubestipulante: params.idSubestipulante,
    idProtocolo: params.idProtocolo,
    listaFaturamento: [
      {
        numeroLinhaRegistro: '1',
        matricula: params.formValues.matricula.trim(),
        tipoSegurado: 'P',
        nome: params.formValues.nome.trim(),
        dataNascimento: formatFormDateToApi(params.formValues.dataNascimento),
        sexo: mapSexToPremiumCode(params.formValues.sexo),
        dataInicioVigencia: dataInclusao,
        anoMesFaturamento: dataInclusao.slice(0, 6),
        cpf: params.formValues.cpf.replace(/\D/g, ''),
        valorCapital: formatSalaryDecimalForApi(params.formValues.capital),
        valorSalario: formatSalaryDecimalForApi(params.formValues.salario),
        dataInclusao,
        infoConjugue: false,
      },
    ],
  };
};

export const resolveValorPremioTotal = (
  response: CalculateProponentPremiumResponse | CalculateProponentPremiumResponse[],
): number => {
  const payload = Array.isArray(response) ? response[0] : response;
  const valorPremioTotal = payload?.retorno?.[0]?.valorPremioTotal;
  return typeof valorPremioTotal === 'number' && Number.isFinite(valorPremioTotal)
    ? valorPremioTotal
    : 0;
};

export const mapFormValuesToNewProponentInclusionItem = (
  values: ProponentFormValues,
  vrPremioSeg?: string,
): ProponentInclusionItem => {
  const cpfDigits = values.cpf.replace(/\D/g, '');

  return {
    nmProponente: values.nome.trim(),
    cdCpf: cpfDigits.slice(0, 9),
    cdCtrlCpf: cpfDigits.slice(9, 11),
    nuDocto: values.documento.trim(),
    cdSexo: mapSexLabelToCode(values.sexo, '1'),
    cdEstCivil: Number(values.estadoCivil) || 0,
    dtNascto: formatFormDateToApi(values.dataNascimento),
    cepPrefixo: parseCepParts(values.cep).cepPrefixo || '0',
    cepSuxifo: parseCepParts(values.cep).cepSufixo || '0',
    dsLogradouro: values.logradouro.trim(),
    nuLogradouro: values.numeroLogradouro.trim() || '0',
    dsComplemento: values.complemento.trim(),
    dsBairro: values.bairro.trim(),
    dsCidade: values.cidade.trim(),
    dsUF: values.uf.trim(),
    cdMatricula: values.matricula.trim(),
    dsCargo: values.cargo.trim(),
    vrCapitalSeg: formatSalaryDecimalForApi(values.capital),
    vrPremioSeg: vrPremioSeg ?? formatMoneyDecimalForApi(values.premio),
    vrSalario: formatSalaryDecimalForApi(values.salario),
  };
};

export const buildAddProponentDataRequest = (params: {
  identificadorSubestipulante: number;
  codigoUsuarioInclusao: string;
  formValues: ProponentFormValues;
  origemInformacao?: number;
  cdPerfilAcesso?: number;
  beneficiarios?: BeneficiaryInclusionItem[];
  vrPremioSeg?: string;
}): AddProponentDataRequest => ({
  identificadorSubestipulante: params.identificadorSubestipulante,
  codigoUsuarioInclusao: params.codigoUsuarioInclusao,
  origemInformacao: params.origemInformacao ?? DEFAULT_PROPONENT_ORIGEM_INFORMACAO,
  cdPerfilAcesso: params.cdPerfilAcesso ?? DEFAULT_PROPONENT_CD_PERFIL_ACESSO,
  jsonProponente: JSON.stringify([
    mapFormValuesToNewProponentInclusionItem(params.formValues, params.vrPremioSeg),
  ]),
  jsonBeneficiario: JSON.stringify(params.beneficiarios ?? []),
});

export const mapProponentDetailToFormValues = (
  detail: ProponentDetail,
): ProponentFormValues => {
  return {
    nome: detail.nomeProponente.trim(),
    matricula: detail.matricula.trim(),
    cpf: formatCpfFromParts(detail.cpf, detail.cpfControle),
    sexo: detail.sexo.trim(),
    documento: detail.rg?.trim() ?? '',
    estadoCivil: detail.estadoCivil != null ? String(detail.estadoCivil) : '',
    dataNascimento: formatDateForDateInput(detail.dataNascimento),
    cep: formatCepForInput(detail.cep),
    logradouro: detail.endereco.trim(),
    numeroLogradouro: detail.numero?.trim() || '0',
    complemento: detail.complemento?.trim() ?? '',
    bairro: detail.bairro.trim(),
    cidade: detail.cidade.trim(),
    uf: detail.estado.trim(),
    cargo: detail.cargo?.trim() ?? '',
    capital: formatSalaryForFormInitial(detail.capitalSegurado),
    premio: formatMoneyForFormDisplay(detail.premioSegurado),
    salario: formatSalaryForFormInitial(detail.salario),
  };
};

export const mapProponentListItemToTableRow = (
  item: ProponentListItem,
): InclusionProponenteTableItem => ({
  identSubestipulante: item.identSubestipulante,
  identProponente: item.identProponente,
  status: mapProponentStatus(item.statusProponente),
  cpf: item.cpf,
  nome: item.nome,
  sexo: mapProponentSex(item.sexo),
  nascimento: formatProponentBirthDate(item.dtNascimento),
  matricula: item.matricula,
  cargoFuncao: item.cargoFuncao,
});

export const buildProponentListSearchParams = (
  search: string,
): Pick<GetProponentListParams, 'nmProponente' | 'cdCpf' | 'cdCtrlCpf' | 'cdMatricula'> => {
  const term = search.trim();
  if (term === '') {
    return {};
  }

  const digitsOnly = term.replace(/\D/g, '');
  if (digitsOnly.length === 11) {
    return {
      cdCpf: digitsOnly.slice(0, 9),
      cdCtrlCpf: digitsOnly.slice(9),
    };
  }

  if (/^\d+$/.test(term)) {
    return { cdMatricula: term };
  }

  return { nmProponente: term };
};

export type ProponentesPanelFilters = {
  status: string;
  cpf: string;
  nome: string;
  sexo: string;
  nascimento: string;
  matricula: string;
  cargoFuncao: string;
};

export const EMPTY_PROPONENTES_PANEL_FILTERS: ProponentesPanelFilters = {
  status: '',
  cpf: '',
  nome: '',
  sexo: '',
  nascimento: '',
  matricula: '',
  cargoFuncao: '',
};

export const filterProponentTableBySearch = (
  rows: InclusionProponenteTableItem[],
  searchTerm: string,
): InclusionProponenteTableItem[] => {
  const term = searchTerm.trim().toLowerCase();
  if (term === '') {
    return rows;
  }

  const termDigits = term.replace(/\D/g, '');

  return rows.filter((row) => {
    const cpfDigits = row.cpf.replace(/\D/g, '');

    return (
      row.status.toLowerCase().includes(term)
      || row.nome.toLowerCase().includes(term)
      || row.cpf.toLowerCase().includes(term)
      || (termDigits !== '' && cpfDigits.includes(termDigits))
      || row.matricula.toLowerCase().includes(term)
      || row.sexo.toLowerCase().includes(term)
      || row.nascimento.toLowerCase().includes(term)
      || row.cargoFuncao.toLowerCase().includes(term)
    );
  });
};

const matchesPanelTextFilter = (fieldValue: string, filterValue: string): boolean => {
  const term = filterValue.trim();
  if (term === '') {
    return true;
  }
  return fieldValue.toLowerCase().includes(term.toLowerCase());
};

export const filterProponentTableRows = (
  rows: InclusionProponenteTableItem[],
  filters: ProponentesPanelFilters | null,
): InclusionProponenteTableItem[] => {
  if (!filters) {
    return rows;
  }

  const hasAnyFilter = Object.values(filters).some((value) => value.trim() !== '');
  if (!hasAnyFilter) {
    return rows;
  }

  return rows.filter((row) =>
    matchesPanelTextFilter(row.status, filters.status)
    && matchesPanelTextFilter(row.cpf, filters.cpf)
    && matchesPanelTextFilter(row.nome, filters.nome)
    && matchesPanelTextFilter(row.sexo, filters.sexo)
    && matchesPanelTextFilter(row.nascimento, filters.nascimento)
    && matchesPanelTextFilter(row.matricula, filters.matricula)
    && matchesPanelTextFilter(row.cargoFuncao, filters.cargoFuncao));
};

export const sortProponentTableRows = (
  rows: InclusionProponenteTableItem[],
  sortBy: string,
): InclusionProponenteTableItem[] => {
  if (!sortBy) {
    return rows;
  }

  return [...rows].sort((a, b) => {
    switch (sortBy) {
      case 'status': return a.status.localeCompare(b.status);
      case 'status-desc': return b.status.localeCompare(a.status);
      case 'cpf': return a.cpf.localeCompare(b.cpf);
      case 'cpf-desc': return b.cpf.localeCompare(a.cpf);
      case 'nome': return a.nome.localeCompare(b.nome);
      case 'nome-desc': return b.nome.localeCompare(b.nome);
      case 'sexo': return a.sexo.localeCompare(b.sexo);
      case 'sexo-desc': return b.sexo.localeCompare(b.sexo);
      case 'nascimento': return a.nascimento.localeCompare(b.nascimento);
      case 'nascimento-desc': return b.nascimento.localeCompare(a.nascimento);
      case 'matricula': return a.matricula.localeCompare(b.matricula);
      case 'matricula-desc': return b.matricula.localeCompare(a.matricula);
      case 'cargoFuncao': return a.cargoFuncao.localeCompare(b.cargoFuncao);
      case 'cargoFuncao-desc': return b.cargoFuncao.localeCompare(a.cargoFuncao);
      default: return 0;
    }
  });
};

type ExternalServiceErrorPayload = {
  retorno?: ProponentDataReturn | ProponentDataReturn[] | null;
  mensagem?: string | null;
};

export class ProponentInconsistencyError extends Error {
  readonly inconsistencies: ProponentDataReturn[];
  readonly status = 400;

  constructor(inconsistencies: ProponentDataReturn[], cause?: unknown) {
    super('Inconsistências encontradas ao incluir proponente');
    this.name = 'ProponentInconsistencyError';
    this.inconsistencies = inconsistencies;

    if (cause instanceof Error) {
      this.cause = cause;
    }
  }
}

const unescapeJsonString = (value: string): string => (
  value
    .replace(/\\"/g, '"')
    .replace(/\\\\/g, '\\')
    .replace(/\\n/g, '\n')
    .replace(/\\r/g, '\r')
    .replace(/\\t/g, '\t')
);

const normalizeRetornoItems = (
  retorno: ProponentDataReturn | ProponentDataReturn[] | null | undefined,
): ProponentDataReturn[] => {
  if (!retorno) {
    return [];
  }

  const items = Array.isArray(retorno) ? retorno : [retorno];

  return items.filter((item) => Boolean(item.tipoInconsistencia?.trim()));
};

/** Formats backend `mensagem` for display: strips `Exception:` and trailing technical detail after ` - `. */
export const formatProponentServiceMensagem = (mensagem: string): string => {
  let text = mensagem.trim().replace(/^Exception:\s*/i, '');
  const detailSeparator = text.indexOf(' - ');
  if (detailSeparator !== -1) {
    text = text.slice(0, detailSeparator).trim();
  }
  return text;
};

const buildMensagemInconsistency = (mensagem: string): ProponentDataReturn => ({
  numeroRegistro: null,
  nomeCampo: null,
  tipoInconsistencia: formatProponentServiceMensagem(mensagem),
  conteudo: null,
  identProponente: null,
});

const extractPayloadInconsistencies = (
  payload: ExternalServiceErrorPayload | null | undefined,
): ProponentDataReturn[] => {
  if (!payload) {
    return [];
  }

  const fromRetorno = normalizeRetornoItems(payload.retorno);
  if (fromRetorno.length > 0) {
    return fromRetorno;
  }

  const mensagem = payload.mensagem?.trim();
  if (!mensagem) {
    return [];
  }

  return [buildMensagemInconsistency(mensagem)];
};

const tryParseExternalErrorJson = (raw: string): ProponentDataReturn[] => {
  try {
    const parsed = JSON.parse(raw) as
      | ExternalServiceErrorPayload
      | ExternalServiceErrorPayload[]
      | ExternalServiceErrorPayload[][];

    if (Array.isArray(parsed) && Array.isArray(parsed[0])) {
      return (parsed as ExternalServiceErrorPayload[][]).flatMap((group) =>
        group.flatMap((payload) => extractPayloadInconsistencies(payload)),
      );
    }

    if (Array.isArray(parsed)) {
      return parsed.flatMap((payload) => {
        if (Array.isArray(payload)) {
          return payload.flatMap((item) => extractPayloadInconsistencies(item));
        }

        return extractPayloadInconsistencies(payload);
      });
    }

    return extractPayloadInconsistencies(parsed);
  } catch {
    return [];
  }
};

const unwrapNestedErrorMessage = (message: string): string => {
  const trimmed = message.trim();
  if (!trimmed.startsWith('{')) {
    return message;
  }

  try {
    const parsed = JSON.parse(trimmed) as { message?: string; error?: string };
    if (typeof parsed.message === 'string' && parsed.message.trim()) {
      return parsed.message;
    }
    if (typeof parsed.error === 'string' && parsed.error.trim()) {
      return parsed.error;
    }
  } catch {
    // Keep original message when not a JSON envelope.
  }

  return message;
};

const extractInconsistenciesWithRegex = (message: string): ProponentDataReturn[] => {
  const results: ProponentDataReturn[] = [];
  const normalizedMessage = message.replace(/\\"/g, '"');
  const pattern =
    /"numeroRegistro"\s*:\s*(\d+|null)\s*,\s*"nomeCampo"\s*:\s*"((?:\\.|[^"\\])*)"\s*,\s*"tipoInconsistencia"\s*:\s*"((?:\\.|[^"\\])*)"/g;

  let match = pattern.exec(normalizedMessage);
  while (match) {
    results.push({
      numeroRegistro: match[1] === 'null' ? null : Number(match[1]),
      nomeCampo: unescapeJsonString(match[2]),
      tipoInconsistencia: unescapeJsonString(match[3]),
      conteudo: null,
      identProponente: null,
    });
    match = pattern.exec(normalizedMessage);
  }

  if (results.length > 0) {
    return results;
  }

  const mensagemPattern =
    /"retorno"\s*:\s*null\s*,\s*"mensagem"\s*:\s*"((?:\\.|[^"\\])*)"/g;
  let mensagemMatch = mensagemPattern.exec(normalizedMessage);
  while (mensagemMatch) {
    const mensagem = unescapeJsonString(mensagemMatch[1]).trim();
    if (mensagem) {
      results.push(buildMensagemInconsistency(mensagem));
    }
    mensagemMatch = mensagemPattern.exec(normalizedMessage);
  }

  return results;
};

const BYTES_MARKER_PATTERN = /^\(\d+\s+bytes\)$/;
const BYTES_MARKER_TOKEN = 'bytes)';

const isBytesSuffixTail = (remainder: string): boolean => {
  if (remainder.length === 0) {
    return true;
  }

  let end = remainder.length;
  if (remainder[end - 1] === ']') {
    end -= 1;
  }

  for (let index = 0; index < end; index += 1) {
    if (remainder[index] === ']') {
      return false;
    }
  }

  return true;
};

const stripTrailingBytesSuffix = (value: string): string => {
  let closeIdx = value.lastIndexOf(BYTES_MARKER_TOKEN);

  while (closeIdx !== -1) {
    let openIdx = closeIdx;
    while (openIdx > 0 && value[openIdx - 1] !== '(') {
      openIdx -= 1;
    }

    if (openIdx > 0 && value[openIdx - 1] === '(') {
      const markerStart = openIdx - 1;
      const markerEnd = closeIdx + BYTES_MARKER_TOKEN.length;
      const marker = value.slice(markerStart, markerEnd);

      if (BYTES_MARKER_PATTERN.test(marker) && isBytesSuffixTail(value.slice(markerEnd))) {
        let stripStart = markerStart;
        while (stripStart > 0 && /\s/.test(value[stripStart - 1]!)) {
          stripStart -= 1;
        }

        return value.slice(0, stripStart);
      }
    }

    closeIdx = value.lastIndexOf(BYTES_MARKER_TOKEN, closeIdx - 1);
  }

  return value;
};

const tryParseInconsistenciesFromPosition = (
  message: string,
  start: number,
): ProponentDataReturn[] => {
  let candidate = stripTrailingBytesSuffix(message.slice(start));

  for (let end = candidate.length; end > 0; end -= 1) {
    const slice = candidate.slice(0, end).trimEnd();
    if (!slice.endsWith('}') && !slice.endsWith(']')) {
      continue;
    }

    const parsedItems = tryParseExternalErrorJson(slice);
    if (parsedItems.length > 0) {
      return parsedItems;
    }
  }

  return extractInconsistenciesWithRegex(message);
};

export const parseProponentInconsistenciesFromError = (
  error: unknown,
): ProponentDataReturn[] => {
  if (error instanceof ProponentInconsistencyError) {
    return error.inconsistencies;
  }

  if (!(error instanceof Error)) {
    return [];
  }

  const message = unwrapNestedErrorMessage(error.message);
  const jsonArrayStart = message.indexOf('[{');
  if (jsonArrayStart !== -1) {
    const parsedFromArray = tryParseInconsistenciesFromPosition(message, jsonArrayStart);
    if (parsedFromArray.length > 0) {
      return parsedFromArray;
    }
  }

  const jsonObjectStart = message.indexOf('{"retorno"');
  if (jsonObjectStart !== -1) {
    const parsedFromObject = tryParseInconsistenciesFromPosition(message, jsonObjectStart);
    if (parsedFromObject.length > 0) {
      return parsedFromObject;
    }
  }

  return extractInconsistenciesWithRegex(message);
};

export const isProponentInconsistencyError = (error: unknown): error is ProponentInconsistencyError => (
  error instanceof ProponentInconsistencyError
);
